import React, { useState, useEffect, useCallback } from 'react';
import CustomButton from './CustomButton';
import { CategoryCard } from './CategoryCard';
import { api, Category, PetSize } from '../services/api';
import { useRegister } from '../context/RegisterContext';
import ForwardArrowIcon from '@mui/icons-material/ArrowForward';
import './StepCategoria.css';

interface Props {
  onNext: () => void;
  onBack: () => void;
}

const PET_SIZE_ORDER: PetSize[] = ['PP', 'P', 'M', 'G', 'GG'];

const PET_SIZE_LABELS: Record<PetSize, { description: string; heat: number }> = {
  PP: { description: 'Cães de até 3kg (ex: Chihuahua, Yorkshire)', heat: 1 },
  P:  { description: 'Cães de 3kg a 8kg (ex: Poodle, Maltês)',    heat: 2 },
  M:  { description: 'Cães de 8kg a 15kg (ex: Beagle, Cocker)',   heat: 3 },
  G:  { description: 'Cães de 15kg a 30kg (ex: Labrador, Border Collie)', heat: 4 },
  GG: { description: 'Cães acima de 30kg (ex: Golden, Husky)',    heat: 5 },
};

const StepCategoria: React.FC<Props> = ({ onNext, onBack }) => {
  const { updateCategory, formData } = useRegister();
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    formData.category.id ?? null
  );
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const data = await api.getPublicCategories();
      setCategories(data);
      if (data.length === 1) {
        setSelectedCategoryId(prev => {
          if (prev === null) {
            updateCategory({ id: data[0].id, name: data[0].name });
            return data[0].id;
          }
          return prev;
        });
      }
    } catch {
      setFetchError('Não foi possível carregar os planos. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [updateCategory]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleSelectCategory = useCallback((category: Category) => {
    setSelectedCategoryId(category.id);
    updateCategory({ id: category.id, name: category.name });
  }, [updateCategory]);

  const selectedCategory = categories.find(c => c.id === selectedCategoryId);

  const sortedTransfers = [...(selectedCategory?.transfers ?? [])].sort(
    (a, b) => PET_SIZE_ORDER.indexOf(a.pet_size) - PET_SIZE_ORDER.indexOf(b.pet_size)
  );

  return (
    <div className="step-categoria">
      <h2 className="step-categoria-title">Selecione seu plano</h2>
      <p className="step-categoria-subtitle">Escolha o plano disponível para o seu petshop e veja o repasse por porte.</p>

      {loading && (
        <div className="loading-state">Carregando planos...</div>
      )}

      {fetchError && !loading && (
        <div className="error-message-box">
          {fetchError}
          <CustomButton
            variant="outlined"
            onClick={fetchCategories}
            sx={{ borderColor: '#FF914D', color: '#FF914D', marginTop: '12px', display: 'block' }}
          >
            Tentar novamente
          </CustomButton>
        </div>
      )}

      {!loading && !fetchError && (
        <>
          {categories.length > 1 && (
            <div className="categories-grid">
              {categories.map(category => (
                <CategoryCard
                  key={category.id}
                  category={category}
                  isSelected={selectedCategoryId === category.id}
                  onSelect={handleSelectCategory}
                />
              ))}
            </div>
          )}

          {selectedCategory && (
            <div className="rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-8">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-gray-500 font-semibold uppercase text-xs tracking-wide">Porte</th>
                    <th className="text-left py-3 px-4 text-gray-500 font-semibold uppercase text-xs tracking-wide hidden sm:table-cell">Tamanho</th>
                    <th className="text-right py-3 px-4 text-gray-500 font-semibold uppercase text-xs tracking-wide">Repasse / banho</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTransfers.map((transfer, index) => {
                    const label = PET_SIZE_LABELS[transfer.pet_size];
                    return (
                      <tr
                        key={transfer.pet_size}
                        className={`border-b border-gray-100 last:border-0 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                      >
                        <td className="py-4 px-4">
                          <div className="flex flex-col gap-1">
                            <span className="font-bold text-[#2f3857] text-base">{transfer.pet_size}</span>
                            <div className="flex gap-[3px]">
                              {Array.from({ length: 5 }, (_, i) => (
                                <div
                                  key={i}
                                  className={`rounded-sm w-3 h-[5px] ${i < label.heat ? 'bg-green-500' : 'bg-gray-200'}`}
                                />
                              ))}
                            </div>
                            <span className="text-xs text-gray-400 sm:hidden">{label.description}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-gray-500 hidden sm:table-cell">{label.description}</td>
                        <td className="py-4 px-4 text-right">
                          <span className="font-bold text-green-600 text-base">
                            {transfer.value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!selectedCategory && categories.length > 0 && (
            <p className="loading-state">Selecione um plano acima para ver a tabela de repasse.</p>
          )}
        </>
      )}

      <div className="form-actions form-actions-two">
        <CustomButton
          type="button"
          variant="outlined"
          onClick={onBack}
          sx={{
            borderColor: '#FF914D',
            color: '#FF914D',
            width: '150px',
            '&:hover': {
              borderColor: '#FF914D',
              backgroundColor: 'rgba(255, 145, 77, 0.04)',
              color: '#FF914D',
            },
          }}
        >
          Voltar
        </CustomButton>
        <CustomButton
          type="button"
          variant="contained"
          customBackground="#46558D"
          sx={{ whiteSpace: 'nowrap', width: 'auto' }}
          onClick={onNext}
          disabled={selectedCategoryId === null || loading}
        >
          Continuar
          <ForwardArrowIcon fontSize="small" />
        </CustomButton>
      </div>
    </div>
  );
};

export default StepCategoria;
